import { DocumentNode, useMutation } from '@apollo/client';
import { noop } from 'lodash';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

export interface JarabotTrackingContextType {
	trackJarabotDropoff: (
		promotionId: string,
		screenName: string | undefined,
		applicationSlug?: string
	) => void;
}

const JarabotTrackingContext = createContext<JarabotTrackingContextType | undefined>(undefined);

export const useJarabotTracking = (): JarabotTrackingContextType => {
	const ctx = useContext(JarabotTrackingContext);
	if (!ctx) throw new Error('useJarabotTracking must be used within a JarabotTrackingProvider');
	return ctx;
};

interface BuildVariablesParams {
	promotionId: string;
	screenName: string;
	applicationSlug?: string;
}

interface JarabotTrackingProviderProps {
	children: React.ReactNode;
	mutation: DocumentNode;
	buildVariables: (params: BuildVariablesParams) => Record<string, unknown> | null;
}

export function JarabotTrackingProvider({
	children,
	mutation,
	buildVariables,
}: JarabotTrackingProviderProps): JSX.Element {
	const [trackMutation] = useMutation(mutation);

	const trackJarabotDropoff = useCallback(
		(promotionId: string, screenName: string | undefined, applicationSlug?: string) => {
			window.dataLayer?.push({ event: promotionId });

			const variables = buildVariables({
				promotionId,
				screenName: screenName ?? 'unknown',
				applicationSlug,
			});
			if (!variables) return;

			trackMutation({ variables }).catch(noop);
		},
		[trackMutation, buildVariables]
	);

	const value = useMemo<JarabotTrackingContextType>(
		() => ({ trackJarabotDropoff }),
		[trackJarabotDropoff]
	);

	return (
		<JarabotTrackingContext.Provider value={value}>{children}</JarabotTrackingContext.Provider>
	);
}
